import React, { useState, useMemo } from 'react';
import { SEO } from './SEO';
import '../styles/skills.css';  // Reuse the same CSS

interface Spirit {
  name: string;
  type: string;
  tags: string;
  description: string;
  effects: string[];
}

export const Spirits: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 21;
  const spirits = require('../data/all_spirits.json') as Spirit[];

  // Filter spirits based on search term
  const filteredSpirits = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return spirits.filter(spirit => 
      (spirit.name?.toLowerCase() || '').includes(searchLower) ||
      (spirit.type?.toLowerCase() || '').includes(searchLower) ||
      (spirit.tags?.toLowerCase() || '').includes(searchLower) ||
      (spirit.description?.toLowerCase() || '').includes(searchLower)
    );
  }, [spirits, searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSpirits.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSpirits = filteredSpirits.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <SEO 
        title="POE2 Spirit Gems"
        description="Complete database of Path of Exile 2 spirit gems. Find detailed information about spirit gem mechanics and effects."
        canonicalUrl="https://www.poe2skills-ai.com/spirits"
      />
      <div className="skills-container">
        <h1>PoE2 Spirit Gems</h1>
        
        <div className="search-container">
          <input
            type="text"
            placeholder="Search spirit gems by name, type, or tags..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            className="search-input"
          />
        </div>

        <div className="skills-grid">
          {currentSpirits.length > 0 ? (
            currentSpirits.map((spirit, index) => (
              <div key={`${spirit.name}-${index}`} className="skill-card">
                <div className="skill-type">{spirit.type}</div>
                <h2>{spirit.name}</h2>
                <p className="skill-description">{spirit.description}</p>
                <div className="skill-tags">
                  {spirit.tags.split(', ').filter(tag => tag).map((tag, tagIndex) => (
                    <span key={`${tag}-${tagIndex}`} className="skill-tag">{tag}</span>
                  ))}
                </div>
                <div className="skill-meta">
                  {spirit.effects.map((effect, effectIndex) => (
                    <div key={`${effect}-${effectIndex}`} className="skill-effects">
                      {effect}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              No spirit gems found matching "{searchTerm}"
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`pagination-button ${pageNum === currentPage ? 'active' : ''}`}
              >
                {pageNum}
              </button>
            ))}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}; 